import type { Components, Theme } from '@mui/material/styles';
import { tabClasses } from '@mui/material/Tab';

// ----------------------------------------------------------------------

const MuiTabs: Components<Theme>['MuiTabs'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  // No `allowScrollButtonsMobile`: on phones we drop MUI's scroll arrows (they also reserve
  // ~40px that pushes the first tab off the left edge); the tabs scroll by swipe. Desktop keeps
  // the auto scroll buttons.
  defaultProps: { textColor: 'inherit', variant: 'scrollable' },

  /** **************************************
   * STYLE
   *************************************** */
  // v9 renamed the `flexContainer` style key to `list` (Tabs now renders
  // a semantic <ul role="tablist">).
  styleOverrides: {
    root: {
      minHeight: '40px',
    },
    list: ({ ownerState, theme }) => ({
      ...(ownerState.variant !== 'fullWidth' && {
        gap: '24px',
        [theme.breakpoints.up('sm')]: { gap: '30px' },
        // Pills carry their own padding on mobile, so the inter-tab gap shrinks to keep the
        // scannable strip tight — a segmented-control look rather than widely-spaced text.
        [theme.breakpoints.down('sm')]: { gap: '6px' },
      }),
    }),
    indicator: ({ theme }) => ({
      backgroundColor: `${theme.vars.palette.text.primary} !important`,
      // Mobile uses a filled pill for the active tab instead of the underline bar, so the
      // indicator is hidden below sm (see the MuiTab pill styles).
      [theme.breakpoints.down('sm')]: { display: 'none' },
    }),
  },
};

// ----------------------------------------------------------------------

const MuiTab: Components<Theme>['MuiTab'] = {
  /** **************************************
   * DEFAULT PROPS
   *************************************** */
  defaultProps: { disableRipple: true, iconPosition: 'start' },

  /** **************************************
   * STYLE
   *************************************** */
  styleOverrides: {
    root: ({ theme }) => ({
      opacity: 1,
      minWidth: 35,
      minHeight: 35,
      padding: theme.spacing(0.5, 0),
      color: theme.vars.palette.text.secondary,
      fontSize: theme.typography.pxToRem(14),
      fontWeight: theme.typography.fontWeightMedium,
      lineHeight: theme.typography.body2.lineHeight,
      [`&.${tabClasses.selected}`]: {
        color: theme.vars.palette.text.primary,
        fontWeight: theme.typography.fontWeightSemiBold,
      },
      // On phones the bare underline tab is a small, easily-missed tap target. Render each
      // tab as a rounded pill — generous padding for a bigger hit area, a filled background
      // on the active tab for an unmistakable selected state — while desktop keeps the
      // underline. Purely presentational; scroll and label behaviour are unchanged.
      [theme.breakpoints.down('sm')]: {
        minHeight: 38,
        padding: theme.spacing(0.75, 1.5),
        borderRadius: theme.spacing(1.25),
        // Transparent border on every tab so the selected pill's visible border doesn't
        // change its box size (and nudge neighbours) when it toggles on.
        border: `1px solid transparent`,
        transition: theme.transitions.create(['background-color', 'border-color']),
        [`&.${tabClasses.selected}`]: {
          color: theme.vars.palette.text.primary,
          backgroundColor: theme.vars.palette.action.selected,
          borderColor: theme.vars.palette.divider,
        },
      },
    }),
  },
};

// ----------------------------------------------------------------------

export const tabs = { MuiTabs, MuiTab };
